import React, {useEffect, useState, useRef} from "react";
import CustomPortal from "../customPortal/CustomPortal";
import {generateClassName, ObjTest} from "jzfz-web-utils";
import CopyBlock from "../copyBlock/CopyBlock";
import {
    CloseOutlined,
    LeftOutlined,
    RightOutlined,
    VerticalRightOutlined,
    VerticalLeftOutlined,
} from '@ant-design/icons';
import './style/customViewer.less';
import {message, Spin} from "antd";
import WaterMark from "../waterMark/WaterMark";
import {MouseIcon} from "../icon/Icon";
import commonConfig from "../../config/common";


export enum ViewerDirectionEnum {
    PREV = 'prev',  // 上一张
    NEXT = 'next',  // 下一张
}


interface CustomViewerProps {
    loading?: boolean;
    visible?: boolean;
    images: {src}[];
    activeIndex?: number;   // 当前选中图片下标
    showPageArrow?: boolean;    // 显示上下页箭头
    zoomSpeed?: number; // 缩放速度
    inBody?: boolean;   // 是否以body元素为容器
    onChange?: (index: number) => void;
    onPageChange?: (direction: ViewerDirectionEnum) => void;  // 点击箭头切换页面
    onClose?: () => void;
    showChangeImg?: boolean; // 显示切换图片按钮
    copyEnabled?: boolean;  // 能否复制
    disabledKeyboard?: boolean; // 禁用键盘切换上/下一张
    hiddenWaterMark?: boolean;    // 是否隐藏水印
}


/**
 * 自定义图片预览组件
 * @param props
 * @constructor
 */
const CustomViewer = (props: CustomViewerProps) => {
    const {
        loading = false,
        visible,
        images = [],
        activeIndex,
        showPageArrow,
        zoomSpeed = 0.1,
        inBody = true,
        onChange,
        onPageChange,
        onClose = () => {},
        showChangeImg = true,
        copyEnabled,
        disabledKeyboard,
        hiddenWaterMark,
    } = props;
    const [index, setIndex] = useState<number>(0);
    // 图片位置
    const [posi, setPosi] = useState({x: 0, y: 0});
    // 上次图片位置
    const prevPosi = useRef<{x, y}>({x: 0, y: 0});
    // 上一次鼠标位置
    const prevMousePosi = useRef<{x, y}>({x: 0, y: 0});
    // 开始平移
    const startMove = useRef<boolean>(false);
    // 上一次缩放量
    const prevScale = useRef<number>(1);
    // 图片缩放量
    const [scale, setScale] = useState<number>(1);
    // 图片容器引用
    const imgViewerRef = useRef<any>(null);
    // 图片引用
    const imgRef = useRef<any>(null);
    // 缩略图列表引用
    const thumbListRef = useRef<any>(null);
    // 图片原始尺寸
    const imgRealSize = useRef<{width, height}>({width: 0, height: 0});
    // 禁止图片平移、缩放
    const imgDisabled = useRef<boolean>(false);
    // 待预览图片地址
    const [imageSrc, setImageSrc] = useState<string>(null);
    // 容器组件引用
    const containerRef = useRef<any>(null);


    useEffect(() => {
        if (!disabledKeyboard && visible && containerRef.current) {
            containerRef.current.focus();
        }
    }, [visible])


    useEffect(() => {
        if (activeIndex !== undefined) {
            setIndex(activeIndex);
        }
    }, [activeIndex])


    useEffect(() => {
        init();
        return () => {
            reset();
        }
    }, [images, index])


    /**
     * 图片初始化
     */
    const init = () => {
        if (!ObjTest.isNotEmptyArray(images)) {
            return;
        }
        if (!imgViewerRef.current) {
            return;
        }

        const { src } = images[index] || {};

        if (!src) {
            return;
        }

        imgDisabled.current = true;
        getImageNaturalRect(src).then((rect: any) => {
            const { width, height } = rect;
            const { width: viewerWidth, height: viewerHeight } = imgViewerRef.current.getBoundingClientRect();

            let ratio = 1;
            let sizeInfo = {width, height, viewerWidth, viewerHeight};

            // 宽/高超出预览区域
            if (width > viewerWidth || height > viewerHeight) {
                ratio = Math.min(viewerWidth / width, viewerHeight / height);
                sizeInfo = {width: ratio * width, height: ratio * height, viewerWidth, viewerHeight};
            }

            imgRealSize.current = {width, height};
            imgDisabled.current = false;
            setImageSrc(src);
            // 缩放
            prevScale.current = ratio;
            setScale(ratio);
            // 图片居中
            imgCentered(sizeInfo);
        })
    }


    /**
     * 获取图片真实尺寸
     * @param src
     */
    const getImageNaturalRect = (src: string): Promise<{width, height}> => {
        return new Promise((resolve: any, reject: any) => {
            let img = new Image();
            img.onload = () => {
                resolve({width: img.naturalWidth, height: img.naturalHeight});
            }
            img.onerror = () => {
                reject();
            }
            img.src = src;
        })
    }


    /**
     * 重置图片平移、缩放状态
     */
    const reset = () => {
        const defaultValus = {x: 0, y: 0};
        prevPosi.current = {...defaultValus};
        prevMousePosi.current = {...defaultValus};
        setPosi({...defaultValus});
        prevScale.current = 1;
        setScale(1);
        imgRealSize.current = {width: 0, height: 0};
        setImageSrc(null);
    }


    /**
     * 使指定缩略图可见
     * @param activeIndex
     */
    const thumbToView = (activeIndex: number) => {
        if (!thumbListRef.current) {
            return;
        }
        let node = thumbListRef.current.querySelectorAll('img')[activeIndex];
        if (!node) {
            return;
        }
        node.scrollIntoView();
    }


    /**
     * 改变图片下标
     * @param newIndex
     */
    const changeIndex = (newIndex: number) => {
        if (activeIndex !== undefined) {
            if (ObjTest.isFunc(onChange)) {
                onChange(newIndex);
            }
        }else {
            setIndex(newIndex);
        }
    }


    /**
     * 切换上一张、下一张图片
     * @param direction
     */
    const change = (direction: ViewerDirectionEnum) => {
        let newIndex = index;
        if (direction === ViewerDirectionEnum.PREV) {
            newIndex--;
        }
        if (direction === ViewerDirectionEnum.NEXT) {
            newIndex++;
        }
        if (newIndex < 0) {
            message.info('已到第一张');
            return;
        }
        if (newIndex > images.length - 1) {
            message.info('已到最后一张');
            return;
        }

        thumbToView(newIndex);
        changeIndex(newIndex);
    }


    /**
     * 切换上下章节
     * @param direction
     */
    const pageChange = (direction: ViewerDirectionEnum) => {
        if (onPageChange) {
            onPageChange(direction);
        }
    }


    /**
     * 点击缩略图
     * @param index
     */
    const thumbClick = (index: number) => {
        changeIndex(index);
    }


    /**
     * 鼠标按下
     * @param e
     */
    const mouseDown = (e: any) => {
        if (imgDisabled.current) {
            return;
        }

        const { pageX, pageY } = e;
        startMove.current = true;
        prevMousePosi.current = {x: pageX, y: pageY};
    }


    /**
     * 鼠标移动
     * @param e
     */
    const mouseMove = (e: any) => {
        const { pageX, pageY } = e;
        const { x, y } = prevMousePosi.current;
        const { x: imgX, y: imgY } = prevPosi.current;

        if (!startMove.current) {
            return;
        }

        let newPosi = {
            x: imgX + pageX - x,
            y: imgY + pageY - y,
        }

        setPosi(newPosi);
    }


    /**
     * 鼠标抬起
     */
    const mouseUp = () => {
        startMove.current = false;
        prevPosi.current = posi;
        prevScale.current = scale;
    }


    /**
     * 鼠标移出
     */
    const mouseLeave = () => {
        mouseUp();
    }


    /**
     * 滚轮缩放
     * @param e
     */
    const wheel = (e: any) => {
        if (imgDisabled.current) {
            return;
        }
        if (!imgViewerRef.current || !imgRef.current) {
            return;
        }
        if (startMove.current) {
            return;
        }

        const { deltaY, pageX, pageY } = e;
        const { x, y } = prevPosi.current;
        const { top, left, width, height } = imgRef.current.getBoundingClientRect();
        const { width: realWidth, height: realHeight } = imgRealSize.current;

        // 放大
        let newScale = 0;
        if (deltaY < 0) {
            newScale = scale + zoomSpeed;
            setScale(newScale);
        }else {
            newScale = scale - zoomSpeed;
            if (newScale < 0.1) {
                newScale = scale;
            }
            setScale(newScale);
        }

        // 以鼠标位置为中心缩放
        let offset = {
            x: (newScale - prevScale.current) * realWidth * Math.abs(pageX - left) / width,
            y: (newScale - prevScale.current) * realHeight * Math.abs(pageY - top) / height,
        }

        let newPosi = {
            x: x - offset.x,
            y: y - offset.y,
        }

        prevScale.current = newScale;
        prevPosi.current = newPosi;
        setPosi(newPosi);
    }


    /**
     * 使图片居中
     * @param info
     */
    const imgCentered = (info: any) => {
        const { width, height, viewerWidth, viewerHeight } = info;
        let newPosi = {
            x: (viewerWidth - width) / 2,
            y: (viewerHeight - height) / 2
        }
        prevPosi.current = newPosi;
        setPosi(newPosi);
    }


    /**
     * 上下左右键
     */
    const keydown = (e: any) => {
        if (disabledKeyboard) {
            return;
        }
        const { keyCode } = e;
        if (keyCode === 38 || keyCode === 37) {
            change(ViewerDirectionEnum.PREV);
        }
        if (keyCode === 40 || keyCode === 39) {
            change(ViewerDirectionEnum.NEXT);
        }
    }


    const component = (
        <div
            ref={containerRef}
            tabIndex={0}
            className={generateClassName(['custom-viewer-wrapper', inBody ? 'full-viewer' : ''])}
            hidden={!visible}
            onMouseDown={mouseDown}
            onMouseMove={mouseMove}
            onMouseUp={mouseUp}
            onMouseLeave={mouseLeave}
            onWheel={wheel}
            onKeyDown={keydown}
        >
            <WaterMark className="h" {...commonConfig.getWaterMarkProps()} hidden={hiddenWaterMark}>
                <CopyBlock className="w h p40" disabled={copyEnabled}>
                    <div
                        ref={imgViewerRef}
                        className="img-viewer"
                    >
                        {
                            imageSrc && (
                                <img
                                    ref={imgRef}
                                    className="main-img"
                                    src={imageSrc}
                                    alt=""
                                    style={{
                                        transform: `translate(${posi.x}px, ${posi.y}px) scale(${scale}, ${scale})`,
                                    }}
                                />
                            )
                        }
                    </div>
                </CopyBlock>
            </WaterMark>
            {
                !ObjTest.isNotEmptyArray(images) && (
                    <div className="img-empty">
                        <p className="img-empty-title">{loading ? '加载中...' : '暂无图片'}</p>
                    </div>
                )
            }
            {showChangeImg &&
                <>
                    <div className="prev-btn-box">
                        {showPageArrow && (
                            <span
                                className="prev-btn"
                                title="上一章"
                                onClick={() => pageChange(ViewerDirectionEnum.PREV)}
                            >
                                <VerticalRightOutlined />
                            </span>)
                        }
                        <span
                            className={generateClassName(['prev-btn', !onPageChange && index === 0 ? 'disabled' : ''])}
                            title="上一张"
                            onClick={() => change(ViewerDirectionEnum.PREV)}
                            >
                            <LeftOutlined />
                        </span>
                    </div>
                    <div className="next-btn-box">
                        <span
                            className={generateClassName(['next-btn', !onPageChange && index === images.length - 1 ? 'disabled' : ''])}
                            title="下一张"
                            onClick={() => change(ViewerDirectionEnum.NEXT)}
                        >
                            <RightOutlined />
                        </span>
                        {showPageArrow && (
                            <span
                                className="next-btn"
                                title="下一章"
                                onClick={() => pageChange(ViewerDirectionEnum.NEXT)}
                            >
                                <VerticalLeftOutlined />
                            </span>)
                        }
                    </div>
                </>
            }

            <span
                className="close-btn"
                title="关闭"
                onClick={onClose}
            >
                <CloseOutlined />
            </span>
            {
                ObjTest.isNotEmptyArray(images) && images.length > 1 && (
                    <CopyBlock className="previewer-thumb-bar" disabled={copyEnabled}>
                        <div ref={thumbListRef} className="thumb-list">
                            {
                                images.map((v: any, i: number) => (
                                    <img
                                        key={i}
                                        className={generateClassName(['thumb-img', index === i ? 'active' : ''])}
                                        src={v.src}
                                        title={v.title}
                                        onClick={() => thumbClick(i)}
                                    />
                                ))
                            }
                        </div>
                    </CopyBlock>
                )
            }
            <div className="mouse-scale-tips">
                <MouseIcon className="mouse-icon" />
                <span className="tips-title">滚动缩放</span>
            </div>
            <Spin className="loading-wrap" spinning={loading} />
        </div>
    )


    return (
        inBody ? <CustomPortal>{component}</CustomPortal> : component
    )
}


export default CustomViewer;
